import React from 'react';
import { styled } from 'styled-components';

import Icons from '../../Elements/Icons/index.tsx';
import { Media } from '../../theme/Media.tsx';
import { Container } from '../../V2/Components/Container/index.tsx';
import type { ICheckbox } from './Checkbox.tsx';
import Checkbox from './Checkbox.tsx';
import type { DateFieldsProps } from './DateFields.tsx';
import { DateFields } from './DateFields.tsx';
import type { ISearch } from './Search.tsx';
import Search from './Search.tsx';
import type { ISelect } from './Select.tsx';
import Select from './Select.tsx';

export type PageHeaderFC<P = Record<string, unknown>> = React.FC<P> & {
  Select: React.FC<ISelect>;
  Search: React.FC<ISearch>;
  Checkbox: React.FC<ICheckbox>;
  DateFields: React.FC<DateFieldsProps>;
  FiltersGroup: React.FC<IFiltersGroup>;
};

const Back = styled(Icons.Angle)`
  position: absolute;
  right: 100%;
  transform: rotateZ(90deg);
  top: 0;
  bottom: 0;
  margin: auto;
  cursor: pointer;
  @media ${Media.smMin} {
    right: calc(100% + 5px);
  }
`;

export interface IFiltersGroup extends React.PropsWithChildren {
  as?: 'form';
  onSubmit?: () => void;
}

const FiltersGroup = styled.div<IFiltersGroup>`
  min-width: 100%;
  display: flex;
  margin-top: 10px;
  margin-bottom: -20px;
  flex-wrap: wrap;
  justify-content: flex-start;
  position: relative;
  z-index: 3;
  @media ${Media.smMin} {
    align-items: center;
  }
  > * {
    margin-bottom: 10px;
    @media ${Media.smMin} {
      max-width: 290px;
      &:not(:last-child) {
        margin-right: 10px;
      }
    }
  }
  @media ${Media.xsMax} {
    flex-direction: column;
  }
`;

const FlexibleFiltersGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 10px 0 -20px;
  position: relative;
  z-index: 3;
  > * {
    min-width: 220px;
    margin: 0 10px 10px 0;
    @media ${Media.xsMax} {
      width: 100%;
      margin-right: 0;
    }
  }
`;

const Actions = styled.div`
  position: relative;
  z-index: 3;
`;

export const Title = styled.div`
  font-size: 22px;
  line-height: 26px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black_1};
  position: relative;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-height: 94px;
  padding: 20px 0 25px;
  @media ${Media.smMin} {
    justify-content: space-between;
  }
  @media ${Media.xsMax} {
    flex-direction: column;
    justify-content: center;
    padding: 20px 0;
    > * {
      width: 100%;
      text-align: center;
    }
    > *:not(:last-child) {
      margin-bottom: 20px;
    }
  }
`;

const Wrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey10_1};
  margin-bottom: 15px;
  min-height: 95px;
`;

export interface IPageHeader extends React.PropsWithChildren {
  title: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
  onBackClick?: () => void;
  fluid?: boolean;
  dataTestId?: string;
  flexibleFiltersGroup?: boolean;
  externalFiltersGroup?: boolean;
}

const PageHeader: React.FC<IPageHeader> = ({
  dataTestId,
  children,
  className,
  title,
  actions,
  onBackClick,
  fluid = false,
  flexibleFiltersGroup,
  externalFiltersGroup = false,
}) => (
  <Wrapper className={className}>
    <Container fluid={fluid}>
      <Content>
        <Title>
          {onBackClick && <Back size={16} onClick={() => onBackClick()} />}
          {title}
        </Title>
        <Actions>{actions}</Actions>
        {flexibleFiltersGroup && !externalFiltersGroup && <FlexibleFiltersGroup>{children}</FlexibleFiltersGroup>}
        {!flexibleFiltersGroup && !externalFiltersGroup && React.Children.map(children, (c) => c)?.some((c) => !!c) && (
          <FiltersGroup data-testid={dataTestId}>{children}</FiltersGroup>
        )}
        {externalFiltersGroup && !flexibleFiltersGroup && children}
      </Content>
    </Container>
  </Wrapper>
);

const PageHeaderMemo = React.memo(PageHeader);

const PageHeaderWrapper: PageHeaderFC<IPageHeader> = (props) => <PageHeaderMemo {...props} />;

PageHeaderWrapper.Select = Select;
PageHeaderWrapper.Search = Search;
PageHeaderWrapper.Checkbox = Checkbox;
PageHeaderWrapper.DateFields = DateFields;
PageHeaderWrapper.FiltersGroup = FiltersGroup;

export default PageHeaderWrapper;
